import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import { Pill } from "@app/components/shared/Pill";
import {
  listSubscriptions,
  assignSubscription,
  extendSubscription,
  type Subscription,
  type AssignSubscriptionRequest,
  type ExtendSubscriptionRequest,
} from "@app/services/adminPlansApi";
import { listUsers, type User } from "@app/services/adminUsersApi";
import { useAppConfig } from "@app/hooks/useAppConfig";
import {
  getTelegramNotificationSettings,
  updateTelegramNotificationSettings,
  type TelegramNotificationSettings,
} from "@app/services/adminSettingsApi";
import { logger } from "@app/services/logger";
import { PolicyAcceptanceCheckbox } from "@app/components/policy/PolicyAcceptanceCheckbox";
import { getPolicyStatus } from "@app/services/policyApi";
import "./subscriptions.css";

function AssignSubscriptionModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [userId, setUserId] = useState("");
  const [planCode, setPlanCode] = useState<"STANDARD" | "PRO">("STANDARD");
  const [periodDays, setPeriodDays] = useState<number | undefined>(undefined);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [showPolicyCheckbox, setShowPolicyCheckbox] = useState(false);

  const { data: users } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: async () => {
      const result = await listUsers();
      return result;
    },
  });

  const { data: policyStatus } = useQuery({
    queryKey: ["user", "policy", "status", userId],
    queryFn: async () => {
      const result = await getPolicyStatus();
      return result;
    },
    enabled: !!userId, // Only check policy when user is selected
  });

  // Check if risk policy is accepted
  const hasAcceptedRisk = policyStatus?.policies?.risk?.accepted ?? false;

  const assignMutation = useMutation({
    mutationFn: (data: AssignSubscriptionRequest) => assignSubscription(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error) => {
      logger.error("Failed to assign subscription:", error);
      alert(`Failed to assign subscription: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Check if user hasn't accepted risk policy
    if (!hasAcceptedRisk) {
      setShowPolicyCheckbox(true);
      if (!policyAccepted) {
        return; // Don't submit if policy not accepted
      }
    }

    assignMutation.mutate({
      user_id: userId,
      plan_code: planCode,
      period_days: periodDays,
    });
  };

  const handlePolicyAccepted = () => {
    setPolicyAccepted(true);
    setShowPolicyCheckbox(false);
  };

  const handlePolicyRejected = () => {
    setPolicyAccepted(false);
    setShowPolicyCheckbox(false);
  };

  return (
    <div className="modal-overlay">
      <Card title="Assign Subscription" className="modal-card">
        <form onSubmit={handleSubmit} className="assign-subscription-form">
          <label>
            User:
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              required
            >
              <option value="">Select a user</option>
              {users?.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.email} {user.name ? `(${user.name})` : ""}
                </option>
              ))}
            </select>
          </label>
          <label>
            Plan:
            <select
              value={planCode}
              onChange={(e) =>
                setPlanCode(e.target.value as "STANDARD" | "PRO")
              }
              required
            >
              <option value="STANDARD">STANDARD</option>
              <option value="PRO">PRO</option>
            </select>
          </label>
          <label>
            Period (days) - Leave empty for never expires:
            <input
              type="number"
              min="1"
              value={periodDays || ""}
              onChange={(e) =>
                setPeriodDays(
                  e.target.value ? parseInt(e.target.value) : undefined,
                )
              }
              placeholder="Leave empty for unlimited"
            />
          </label>

          {showPolicyCheckbox && userId && (
            <div className="assign-subscription-policy">
              <PolicyAcceptanceCheckbox
                required={true}
                showStatus={false}
                onAccepted={handlePolicyAccepted}
                onRejected={handlePolicyRejected}
              />
            </div>
          )}

          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={assignMutation.isPending || (showPolicyCheckbox && !policyAccepted)}
            >
              Assign Subscription
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function ExtendSubscriptionModal({
  subscription,
  onClose,
  onSuccess,
}: {
  subscription: Subscription;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [periodDays, setPeriodDays] = useState<number>(30);

  const extendMutation = useMutation({
    mutationFn: (data: ExtendSubscriptionRequest) => extendSubscription(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      logger.error("Failed to extend subscription:", error);
      alert(`Failed to extend subscription: ${error.response?.data?.detail || error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    extendMutation.mutate({
      subscription_id: subscription.id,
      period_days: periodDays,
    });
  };

  return (
    <div className="modal-overlay">
      <Card title="Extend Subscription" className="modal-card">
        <form onSubmit={handleSubmit} className="assign-subscription-form">
          <div style={{ marginBottom: "1rem" }}>
            <strong>User ID:</strong> {subscription.user_id}
            <br />
            <strong>Email:</strong> {subscription.user_email || "—"}
            <br />
            <strong>Plan:</strong> {subscription.plan_code} - {subscription.plan_name}
            <br />
            <strong>Current Ends At:</strong>{" "}
            {subscription.ends_at
              ? new Date(subscription.ends_at).toLocaleString("en-US")
              : "Never"}
          </div>
          <label>
            Extend by (days):
            <input
              type="number"
              min="1"
              value={periodDays}
              onChange={(e) => setPeriodDays(parseInt(e.target.value) || 30)}
              required
            />
          </label>

          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={extendMutation.isPending}
            >
              Extend Subscription
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export function SubscriptionsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"list" | "extend" | "notifications">("list");
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);

  const { data: appConfig } = useAppConfig();
  const isAdmin = appConfig?.role === "admin";

  const [telegramSettings, setTelegramSettings] = useState<TelegramNotificationSettings>({
    bot_token: "",
    bot_link: "",
  });

  const { data: telegramSettingsData, refetch: refetchTelegramSettings } = useQuery<TelegramNotificationSettings>({
    queryKey: ["admin", "telegram-notification-settings"],
    queryFn: async () => {
      const result = await getTelegramNotificationSettings();
      return result;
    },
    enabled: isAdmin && activeTab === "notifications",
  });

  const updateTelegramSettingsMutation = useMutation({
    mutationFn: updateTelegramNotificationSettings,
    onSuccess: (data) => {
      logger.info("Telegram settings updated:", data as unknown as Record<string, unknown>);
      refetchTelegramSettings();
    },
    onError: (error) => {
      logger.error("Failed to update telegram settings:", error);
    },
  });

  useEffect(() => {
    if (telegramSettingsData) {
      setTelegramSettings(telegramSettingsData);
    }
  }, [telegramSettingsData]);

  const {
    data: subscriptions,
    isLoading,
    error,
  } = useQuery<Subscription[]>({
    queryKey: ["subscriptions"],
    queryFn: async () => {
      const result = await listSubscriptions();
      return result;
    },
  });

  if (isLoading) {
    return <div>Loading subscriptions...</div>;
  }

  if (error) {
    return <div>Error loading subscriptions: {error.message}</div>;
  }

  const activeSubscriptions = subscriptions?.filter((sub) => sub.status === "active") || [];

  return (
    <div className="subscriptions-root">
      <SectionHeader
        title="Subscriptions Management"
        subtitle="Manage user subscriptions to trading plans."
        actions={
          <Button onClick={() => setShowAssignModal(true)} variant="primary">
            Assign Subscription
          </Button>
        }
      />

      <Card className="subscriptions-list-card">
        <div className="subscriptions-tabs">
          <button
            className={`subscriptions-tab ${activeTab === "list" ? "active" : ""}`}
            onClick={() => setActiveTab("list")}
          >
            All Subscriptions
          </button>
          <button
            className={`subscriptions-tab ${activeTab === "extend" ? "active" : ""}`}
            onClick={() => setActiveTab("extend")}
          >
            Extend Subscription
          </button>
          {isAdmin && (
            <button
              className={`subscriptions-tab ${activeTab === "notifications" ? "active" : ""}`}
              onClick={() => setActiveTab("notifications")}
            >
              Notification Settings
            </button>
          )}
        </div>

        {activeTab === "list" && (
          <>
        {subscriptions && subscriptions.length > 0 ? (
          <table className="subscriptions-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Starts At</th>
                <th>Ends At</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => (
                <tr key={sub.id}>
                  <td>{sub.user_id}</td>
                  <td>{sub.user_email || "—"}</td>
                  <td>
                    <strong>{sub.plan_code}</strong> - {sub.plan_name}
                  </td>
                  <td>
                    <Pill
                      tone={
                        sub.status === "active"
                          ? "success"
                          : sub.status === "expired"
                            ? "danger"
                            : "neutral"
                      }
                    >
                      {sub.status}
                    </Pill>
                  </td>
                  <td>{new Date(sub.starts_at).toLocaleString("en-US")}</td>
                  <td>
                    {sub.ends_at
                      ? new Date(sub.ends_at).toLocaleString("en-US")
                      : "Never"}
                  </td>
                  <td>{new Date(sub.created_at).toLocaleString("en-US")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="subscriptions-empty">
            No subscriptions found. Assign the first subscription to get started.
          </div>
            )}
          </>
        )}

        {activeTab === "extend" && (
          <>
            {activeSubscriptions.length > 0 ? (
              <table className="subscriptions-table">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Email</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Ends At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {activeSubscriptions.map((sub) => (
                    <tr key={sub.id}>
                      <td>{sub.user_id}</td>
                      <td>{sub.user_email || "—"}</td>
                      <td>
                        <strong>{sub.plan_code}</strong> - {sub.plan_name}
                      </td>
                      <td>
                        <Pill tone="success">{sub.status}</Pill>
                      </td>
                      <td>
                        {sub.ends_at
                          ? new Date(sub.ends_at).toLocaleString("en-US")
                          : "Never"}
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => setSelectedSubscription(sub)}
                        >
                          Extend
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="subscriptions-empty">
                No active subscriptions found to extend.
              </div>
            )}
          </>
        )}

        {activeTab === "notifications" && isAdmin && (
          <Card
            title="Telegram Notification Bot Settings"
            subtitle="Configure the shared Telegram bot for sending notifications to users"
          >
            <div style={{ padding: "16px 0" }}>
              <div style={{ marginBottom: "24px" }}>
                <h4 style={{ marginBottom: "8px" }}>Bot Configuration</h4>
                <p style={{ color: "var(--color-text-secondary)", fontSize: "0.875rem" }}>
                  Configure the Telegram bot that will send notifications to all users.
                  Users will connect by clicking the bot link.
                </p>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Bot Token
                  <input
                    type="text"
                    value={telegramSettings.bot_token || ""}
                    onChange={(e) => setTelegramSettings((prev) => ({ ...prev, bot_token: e.target.value }))}
                    placeholder="123456:ABCDEF..."
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "8px 12px",
                      marginTop: "4px",
                      borderRadius: "4px",
                      border: "1px solid var(--color-border)",
                      background: "var(--color-bg-primary)",
                      color: "var(--color-text-primary)",
                    }}
                  />
                </label>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Bot Link (for users)
                  <input
                    type="text"
                    value={telegramSettings.bot_link || ""}
                    onChange={(e) => setTelegramSettings((prev) => ({ ...prev, bot_link: e.target.value }))}
                    placeholder="t.me/YourTradingBot"
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "8px 12px",
                      marginTop: "4px",
                      borderRadius: "4px",
                      border: "1px solid var(--color-border)",
                      background: "var(--color-bg-primary)",
                      color: "var(--color-text-primary)",
                    }}
                  />
                </label>
              </div>

              <Button
                variant="primary"
                onClick={() => updateTelegramSettingsMutation.mutate(telegramSettings)}
                disabled={updateTelegramSettingsMutation.isPending}
              >
                {updateTelegramSettingsMutation.isPending ? "Saving..." : "Save Settings"}
              </Button>

              <div style={{
                background: "var(--color-bg-secondary)",
                padding: "16px",
                borderRadius: "8px",
                marginTop: "24px"
              }}>
                <p style={{ color: "var(--color-text-secondary)", fontSize: "0.875rem" }}>
                  <strong>How it works:</strong>
                </p>
                <ol style={{ color: "var(--color-text-secondary)", fontSize: "0.875rem", marginTop: "8px", paddingLeft: "20px" }}>
                  <li style={{ marginBottom: "4px" }}>Admin sets the Bot Token and Link above</li>
                  <li style={{ marginBottom: "4px" }}>Users click the bot link and press "Start" in Telegram</li>
                  <li style={{ marginBottom: "4px" }}>The bot automatically registers their chat ID</li>
                  <li>Users receive personalized notifications via Telegram</li>
                </ol>
              </div>
            </div>
          </Card>
        )}
      </Card>

      {showAssignModal && (
        <AssignSubscriptionModal
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
            setShowAssignModal(false);
          }}
        />
      )}

      {selectedSubscription && (
        <ExtendSubscriptionModal
          subscription={selectedSubscription}
          onClose={() => setSelectedSubscription(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
            setSelectedSubscription(null);
          }}
        />
      )}
    </div>
  );
}

